import { useEffect, useMemo, useState } from 'react'
import { MRT_PaginationState, type MRT_ColumnDef, MRT_SortingState } from 'mantine-react-table'
import { formatDateWithoutTime, getCurrentTranslation } from '@/shared/lib/utils'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@/shared/lib/useDebounce'
import { useWebinarsByPage } from '@/entities/Webinars/api/webinars-api'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { Checkbox } from '@mantine/core'
import { Webinar } from '@/shared/api/topli/TopliApi'
import { TruncatedTextCell } from '@/shared/ui/TruncatedTextCellProps'

export const useWebinarsTable = (
  onOpenFullDescription: (row: Webinar) => void,
  onOpenShowVideo: (row: Webinar) => void,
) => {
  const [search, setSearch] = useState('')
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const { t } = useTranslation(['webinar', 'common'])
  const { language } = useLanguageStore()
  const memoizedSorting = useMemo(() => {
    let orderBy = ''
    if (!sorting.length) return null
    sorting.forEach(item => (item.desc ? (orderBy = `-${item.id}`) : (orderBy = `${item.id}`)))
    return orderBy
  }, [sorting])
  const debouncedSearch = useDebounce(search, 500)

  const {
    data: webinars,
    isLoading: isLoadingWebinars,
    isError: isErrorWebinars,
    isFetching: isFetchingWebinars,
  } = useWebinarsByPage(
    memoizedSorting,
    pagination.pageSize,
    pagination.pageIndex * pagination.pageSize,
    debouncedSearch,
    language,
  )

  const fetchedWebinars = webinars?.data ?? []
  const totalRowCount = webinars?.total ?? 0

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [debouncedSearch])

  const handleSearchChange = (value: string) => {
    setSearch(value)
  }

  const columns = useMemo<MRT_ColumnDef<Webinar>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 90,
      },
      {
        accessorKey: 'name',
        header: t('webinar:name'),
        Cell: ({ row }) => <div className='text-sm font-semibold'>{row.original.name}</div>,
      },
      {
        accessorKey: 'release_date',
        header: t('webinar:release_date'),
        Cell: ({ row }) =>
          row.original.release_date ? (
            <div>{formatDateWithoutTime(row.original.release_date || null, language)}</div>
          ) : (
            '-'
          ),
      },
      {
        accessorKey: 'description',
        size: 400,
        header: t('webinar:description'),
        Cell: ({ row }) => {
          const currentDescription = getCurrentTranslation(row.original, language, 'description')
          return (
            <TruncatedTextCell
              text={currentDescription}
              onShowMore={() => onOpenFullDescription(row.original)}
            />
          )
        },
      },
      {
        accessorKey: 'video_url',
        header: t('webinar:video_url'),
        enableSorting: false,
        Cell: ({ row }) =>
          row.original.video_url ? (
            <button
              type='button'
              className='bg-transparent border-none text-blue px-0'
              onClick={() => onOpenShowVideo(row.original)}
            >
              {t('common:openLink')}
            </button>
          ) : (
            '-'
          ),
      },
      {
        accessorKey: 'ai_translations',
        header: t('unit:machine_translation'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.has_ai_translation} readOnly />,
      },
    ],
    [language],
  )
  return {
    pagination,
    fetchedWebinars,
    totalRowCount,
    columns,
    isErrorWebinars,
    isFetchingWebinars,
    isLoadingWebinars,
    sorting,
    setSorting,
    setPagination,
    handleSearchChange,
  }
}
